import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';

import { ExamAddressListComponent } from './views/exam-address/exam-address-list/exam-address-list.component';
import { ExamReportListComponent } from './views/exam-report/exam-report-list/exam-report-list.component';
import { SubjectListComponent } from './views/subject/subject-list/subject-list.component';
import { ExaminerListComponent } from './views/examiner/examiner-list/examiner-list.component';
import { UserListComponent } from './views/user/user-list/user-list.component';
import { ExamReportEditComponent } from './views/exam-report/exam-report-edit/exam-report-edit.component';
import { ExamAddressEditComponent } from './views/exam-address/exam-address-edit/exam-address-edit.component';
import { UserNewComponent } from './views/user/user-new/user-new.component';
import { ExamAddressNewComponent } from './views/exam-address/exam-address-new/exam-address-new.component';
import { ExaminerNewComponent } from './views/examiner/examiner-new/examiner-new.component';
import { ExamReportNewComponent } from './views/exam-report/exam-report-new/exam-report-new.component';
import { HomeLayoutComponent } from './layouts/home-layout';
import { ErrorLayoutComponent } from './layouts/error-layout';
import { ExamReportExaminersTabComponent } from './views/exam-report/exam-report-tabs/exam-report-examiners-tab/exam-report-examiners-tab.component';
import { ExamReportDetailsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-details-tab/exam-report-details-tab.component';
import { ExamReportStudentsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-students-tab/exam-report-students-tab.component';
import { ExamReportResultsTabComponent } from './views/exam-report/exam-report-tabs/exam-report-results-tab/exam-report-results-tab.component';
import { ExamResultsDatatableComponent } from './components/datatables/exam-results-datatable/exam-results-datatable.component';
import { ExamSubjectDatatableComponent } from './components/datatables/exam-subject-datatable/exam-subject-datatable.component';
import { StudentNewComponent } from './views/student/student-new/student-new.component';
import { StudentEditComponent } from './views/student/student-edit/student-edit.component';
import { AutoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { HasRoleGuard } from './guards/has-role.guard';
import { RoleConstants } from './constants/roleConstants';
import { HomeComponent } from './components/home/home.component';
import { ErrorComponent } from './components/error/error.component';
import { StudentResultsDatatableComponent } from './components/datatables/student-results-datatable/student-results-datatable.component';
import { EmptyComponent } from './components/empty/empty.component';
import { TenantListComponent } from './views/tenant/tenant-list/tenant-list.component';
import { TenantNewComponent } from './views/tenant/tenant-new/tenant-new.component';
import { TenantEditComponent } from './views/tenant/tenant-edit/tenant-edit.component';
import { UserEditComponent } from './views/user/user-edit/user-edit.component';
import { MissingTenantComponent } from './components/missing-tenant/missing-tenant.component';
import { HasTenantGuard } from './guards/has-tenant.guard';
import { SubjectEditComponent } from './views/subject/subject-edit/subject-edit.component';
import { AppConfigService } from './services/app-config.service';

const routes: Routes = [
  {
    path: '',
    component: HomeLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        component: HomeComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-reports',
        component: ExamReportListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-reports/new',
        component: ExamReportNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-reports/:id',
        component: ExamReportEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
        children: [
          {
            path: 'exam-report-details',
            component: ExamReportDetailsTabComponent,
            canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
            data: {
              roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
            },
          },
          {
            path: 'examiners',
            component: ExamReportExaminersTabComponent,
            canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
            data: {
              roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
            },
          },
          {
            path: 'students',
            component: ExamReportStudentsTabComponent,
            canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
            data: {
              roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
            },
            children: [
              {
                path: ':id',
                component: ExamSubjectDatatableComponent,
                canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
                data: {
                  roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
                },
              },
            ],
          },
          {
            path: 'results',
            component: ExamReportResultsTabComponent,
            canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
            data: {
              roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
            },
            children: [
              {
                path: ':id',
                component: ExamResultsDatatableComponent,
                canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
                data: {
                  roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
                },
              },
            ],
          },
        ],
      },
      {
        path: 'subjects',
        component: SubjectListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'subjects/:id',
        component: SubjectEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-addresses',
        component: ExamAddressListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-addresses/new',
        component: ExamAddressNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'exam-addresses/:id',
        component: ExamAddressEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'examiners',
        component: ExaminerListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'examiners/new',
        component: ExaminerNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'examiners/new/exam-report/:id',
        component: ExaminerNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'student-results',
        component: StudentResultsDatatableComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
        children: [
          {
            path: ':id',
            component: ExamResultsDatatableComponent,
            canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
            data: {
              roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
            },
          },
        ],
      },
      {
        path: 'students/new/exam-report/:examReportId',
        component: StudentNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'students/:studentId/exam-report/:examReportId',
        component: StudentEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.Clerk, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'users',
        component: UserListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'users/new',
        component: UserNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'users/:id',
        component: UserEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin, RoleConstants.TenantAdmin],
        },
      },
      {
        path: 'tenants',
        component: TenantListComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin],
        },
      },
      {
        path: 'tenants/new',
        component: TenantNewComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin],
        },
      },
      {
        path: 'tenants/:id',
        component: TenantEditComponent,
        canActivate: [AutoLoginPartialRoutesGuard, HasRoleGuard, HasTenantGuard],
        data: {
          roles: [RoleConstants.Admin],
        },
      },
    ],
  },
  { path: 'empty', component: EmptyComponent },
  { path: 'signin-oidc', redirectTo: 'empty' },
  {
    path: '',
    component: ErrorLayoutComponent,
    children: [
      { path: 'error', component: ErrorComponent },
      { path: 'error/:status', component: ErrorComponent },
      { path: 'missing-tenant', component: MissingTenantComponent },
      { path: '**', redirectTo: 'error/404' },
    ],
  },
];

@NgModule({
  imports: [BrowserModule, RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [AppConfigService],
})
export class AppRoutingModule {
  constructor() {}
}
